import { Icon } from "@iconify/react/dist/iconify.js";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { toast } from "react-toastify";
import { loginUser } from "../services/authService";
import { FiEye, FiEyeOff } from "react-icons/fi";
import { useAuth } from "../context/AuthContext";

/* 🔴 UI SAME, FIELD NAME INTERNAL CHANGE ONLY */
const schema = yup.object().shape({
  email: yup
    .string()
    .required("Mobile is required")
    .matches(/^[0-9]{10}$/, "Enter valid 10 digit mobile"),
  password: yup.string().required("Password is required"),
});

const SignInLayer = () => {
  const navigate = useNavigate();
  const { setUser, setPermissions } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
  });

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      // 🔴 email field UI → mobile for API
      const res = await loginUser(data.email, data.password);

      if (res?.status === true) {
        // Hydrate the auth context so sidebar/routes reflect the new session
        // immediately without waiting for the next /me round-trip.
        setUser(res.user || null);
        setPermissions(res.permissions || []);
        toast.success("Login Successful");
        navigate("/");
      } else {
        toast.error(res?.msg || "Invalid mobile or password");
      }
    } catch (err) {
      toast.error("Login failed. Try again.");
    }
    setLoading(false);
  };

  return (
    <section style={styles.page}>
      {/* Decorative blobs */}
      <div style={styles.blobTopLeft} />
      <div style={styles.blobBottomRight} />

      <div style={styles.formCard} className="animate-fade-in-up">
        {/* Logo */}
        <div style={styles.logoWrap}>
          <img
            src="assets/images/LogoNew.png"
            alt="Opinion Games"
            style={styles.logo}
          />
        </div>

        {/* Divider */}
        <div style={styles.divider} />

        {/* Heading */}
        <div style={{ marginBottom: 28, textAlign: "center" }}>
          {/* <h3 style={styles.formTitle}>Admin Sign In</h3>
          <p style={styles.formSub}>Enter your credentials to continue</p> */}
        </div>

        {/* FORM */}
        <form onSubmit={handleSubmit(onSubmit)}>

          {/* Mobile Number */}
          <div style={{ marginBottom: 18 }}>
            <label style={styles.label}>Mobile Number</label>
            <div style={styles.inputWrap}>
              <span style={styles.inputIcon}>
                <Icon icon="solar:phone-calling-bold" style={{ fontSize: 18, color: "#16632b" }} />
              </span>
              <input
                type="text"
                style={{ ...styles.input, ...(errors.email ? styles.inputError : {}) }}
                placeholder="Enter 10-digit mobile number"
                {...register("email")}
              />
            </div>
            {errors.email && (
              <p style={styles.errorMsg}>
                <Icon icon="solar:danger-circle-bold" style={{ marginRight: 4, fontSize: 13 }} />
                {errors.email?.message}
              </p>
            )}
          </div>

          {/* Password */}
          <div style={{ marginBottom: 22 }}>
            <label style={styles.label}>Password</label>
            <div style={styles.inputWrap}>
              <span style={styles.inputIcon}>
                <Icon icon="solar:lock-password-bold" style={{ fontSize: 18, color: "#16632b" }} />
              </span>
              <input
                type={showPassword ? "text" : "password"}
                style={{ ...styles.input, paddingRight: 48, ...(errors.password ? styles.inputError : {}) }}
                placeholder="Enter your password"
                {...register("password")}
              />
              <button type="button" onClick={() => setShowPassword(!showPassword)} style={styles.eyeBtn}>
                {showPassword ? <FiEyeOff size={17} /> : <FiEye size={17} />}
              </button>
            </div>
            {errors.password && (
              <p style={styles.errorMsg}>
                <Icon icon="solar:danger-circle-bold" style={{ marginRight: 4, fontSize: 13 }} />
                {errors.password?.message}
              </p>
            )}
          </div>

          {/* Remember + Forgot */}
          <div style={styles.rememberRow}>
            <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
              <input type="checkbox" id="remember" style={styles.checkbox} />
              <span style={{ fontSize: "0.85rem", color: "#64748b", fontWeight: 500 }}>
                Keep me signed in
              </span>
            </label>
            <Link to="#" style={styles.forgotLink}>Forgot Password?</Link>
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={loading}
            style={{
              ...styles.submitBtn,
              opacity: loading ? 0.8 : 1,
              cursor: loading ? "not-allowed" : "pointer",
            }}
          >
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" style={{ marginRight: 8 }} />
                Signing In...
              </>
            ) : (
              <>
                <Icon icon="solar:login-2-bold" style={{ fontSize: 18, marginRight: 8 }} />
                Sign In to Dashboard
              </>
            )}
          </button>
        </form>

        {/* Footer */}
        <p style={styles.copyright}>© 2024 Opinion Games · All rights reserved</p>
      </div>
    </section>
  );
};

/* ── Inline styles ── */
const styles = {
  page: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "linear-gradient(145deg, #f0faf3 0%, #e8f5ed 50%, #f0f4f8 100%)",
    padding: "24px 16px",
    position: "relative",
    overflow: "hidden",
  },
  blobTopLeft: {
    position: "absolute",
    top: -120,
    left: -120,
    width: 380,
    height: 380,
    borderRadius: "50%",
    background: "radial-gradient(circle, rgba(29,161,68,0.12) 0%, transparent 70%)",
    pointerEvents: "none",
  },
  blobBottomRight: {
    position: "absolute",
    bottom: -100,
    right: -100,
    width: 320,
    height: 320,
    borderRadius: "50%",
    background: "radial-gradient(circle, rgba(22,99,43,0.10) 0%, transparent 70%)",
    pointerEvents: "none",
  },
  formCard: {
    position: "relative",
    zIndex: 1,
    background: "#fff",
    borderRadius: 24,
    padding: "40px 40px 32px",
    width: "100%",
    maxWidth: 420,
    boxShadow: "0 24px 64px rgba(22, 99, 43, 0.10), 0 4px 16px rgba(0,0,0,0.06)",
    border: "1px solid rgba(22, 99, 43, 0.08)",
  },
  logoWrap: {
    display: "flex",
    justifyContent: "center",
    marginBottom: 20,
  },
  logo: {
    height: 52,
    objectFit: "contain",
  },
  divider: {
    height: 1,
    background: "linear-gradient(90deg, transparent, #e2e8f0, transparent)",
    marginBottom: 24,
  },
  formTitle: {
    fontWeight: 800,
    fontSize: "1.5rem",
    color: "#1a1a2e",
    marginBottom: 6,
    letterSpacing: "-0.4px",
  },
  formSub: {
    color: "#94a3b8",
    fontSize: "0.88rem",
    margin: 0,
  },
  label: {
    display: "block",
    fontWeight: 600,
    fontSize: "0.8rem",
    color: "#475569",
    marginBottom: 7,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
  },
  inputWrap: {
    position: "relative",
    display: "flex",
    alignItems: "center",
  },
  inputIcon: {
    position: "absolute",
    left: 13,
    display: "flex",
    alignItems: "center",
    pointerEvents: "none",
    zIndex: 1,
  },
  input: {
    width: "100%",
    padding: "12px 14px 12px 42px",
    border: "1.5px solid #e2e8f0",
    borderRadius: 12,
    fontSize: "0.93rem",
    color: "#1e293b",
    background: "#f8fafc",
    outline: "none",
    transition: "all 0.2s ease",
    fontFamily: "inherit",
  },
  inputError: {
    borderColor: "#ef4444",
    background: "#fff5f5",
  },
  eyeBtn: {
    position: "absolute",
    right: 13,
    background: "none",
    border: "none",
    cursor: "pointer",
    color: "#94a3b8",
    display: "flex",
    alignItems: "center",
    padding: 0,
  },
  errorMsg: {
    display: "flex",
    alignItems: "center",
    color: "#ef4444",
    fontSize: "0.79rem",
    marginTop: 6,
    marginBottom: 0,
    fontWeight: 500,
  },
  rememberRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 24,
  },
  checkbox: {
    accentColor: "#16632b",
    width: 15,
    height: 15,
    cursor: "pointer",
  },
  forgotLink: {
    fontSize: "0.83rem",
    fontWeight: 700,
    color: "#16632b",
    textDecoration: "none",
  },
  submitBtn: {
    width: "100%",
    padding: "13px 20px",
    background: "linear-gradient(135deg, #16632b 0%, #1da144 100%)",
    color: "#fff",
    border: "none",
    borderRadius: 12,
    fontSize: "0.96rem",
    fontWeight: 700,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 8px 24px rgba(22, 99, 43, 0.30)",
    transition: "all 0.25s ease",
    fontFamily: "inherit",
    letterSpacing: "0.01em",
  },
  copyright: {
    textAlign: "center",
    color: "#cbd5e1",
    fontSize: "0.76rem",
    marginTop: 28,
    marginBottom: 0,
  },
};

export default SignInLayer;
